import { useState } from "react";
import { SCHOOL } from "data";
import { useResource } from "hooks/useResource";
import { getSettings } from "services/content";
import { submitContact } from "services/forms";
import PageHero from "components/shared/PageHero";
import SectionLabel from "components/shared/SectionLabel";
import { Button } from "components/ui/button";
import Skeleton from "components/shared/Skeleton";

const FIELDS = [
  { k: "name", l: "Your Name *", t: "text", p: "Full name" },
  { k: "email", l: "Email Address *", t: "email", p: "your@email.com" },
  { k: "subject", l: "Subject *", t: "text", p: "How can we help?" },
];

const inputCls =
  "w-full px-3.5 py-2.5 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none transition-colors focus:border-green";

// Google Maps embed for the campus. Fixed — the location doesn't change.
const MAP_EMBED =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3677.9861360648433!2d89.57037219678955!3d22.802977599999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39ff8fdbc59aecf5%3A0x2abf8c7282ed11e3!2sKhulna%20Collegiate%20Girls'%20School%20%26%20KCC%20Women's%20College!5e0!3m2!1sen!2sbd!4v1782206468544!5m2!1sen!2sbd";

export default function ContactPage() {
  // Single source of truth: admin → Settings → School Profile. The same fields
  // drive the site header and footer, so contact details are edited in one place.
  const { data: settings } = useResource(getSettings);
  const p = settings?.profile;
  const info = !settings
    ? []
    : [
        { icon: "📍", label: "Address", val: p?.address || SCHOOL.address },
        { icon: "📞", label: "Phone (School)", val: p?.phone || SCHOOL.phone },
        { icon: "📞", label: "Phone (College)", val: p?.phoneCollege },
        { icon: "✉", label: "Email", val: p?.email || SCHOOL.email },
        { icon: "🌐", label: "Website", val: p?.website },
        { icon: "🕒", label: "Office Hours", val: p?.officeHours },
      ].filter((r) => r.val); // rows with nothing filled in are simply hidden
  const [msg, setMsg] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await submitContact(msg);
      setSent(true);
      setMsg({ name: "", email: "", subject: "", message: "", website: "" });
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="animate-fadeIn">
      <PageHero
        title="Contact Us"
        sub="We welcome enquiries from prospective students, guardians, and the community."
        page="Contact"
        bg="linear-gradient(140deg,#003d2b,#005C40)"
      />
      <section className="max-w-container mx-auto px-4 sm:px-10 py-[68px]">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-[60px]">
          {/* Info */}
          <div>
            <SectionLabel label="Get in Touch" />
            <h2 className="font-serif text-[32px] font-bold text-green-dark mb-7">
              Contact Information
            </h2>
            {!settings
              ? Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-[68px] mb-5 rounded-xl" />
                ))
              : info.map(({ icon, label, val }) => (
                  <div
                    key={label}
                    className="flex gap-4 mb-5 px-[18px] py-4 bg-white border border-line rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                  >
                    <span className="text-[22px] shrink-0">{icon}</span>
                    <div>
                      <div className="text-xs font-bold text-muted tracking-[0.08em] uppercase mb-[3px]">
                        {label}
                      </div>
                      <div className="text-[15px] text-ink font-medium">
                        {val}
                      </div>
                    </div>
                  </div>
                ))}
            {/* Location map */}
            <div className="rounded-[14px] overflow-hidden border border-line mt-2 h-[260px]">
              <iframe
                title="KCGS & KCC Women's College location"
                src={MAP_EMBED}
                className="w-full h-full"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Message Form */}
          <div>
            <SectionLabel label="Send a Message" color="#8B1A4A" />
            <h2 className="font-serif text-[28px] font-bold text-green-dark mb-5">
              Write to Us
            </h2>
            {sent ? (
              <div className="bg-green-light border-[1.5px] border-green rounded-[14px] p-10 text-center">
                <div className="text-5xl mb-3">✅</div>
                <div className="font-serif text-[22px] font-bold text-green-dark mb-2">
                  Message Sent!
                </div>
                <p className="text-muted">We will respond within 2 working days.</p>
                <button
                  onClick={() => setSent(false)}
                  className="mt-5 bg-green text-white border-none rounded-lg px-6 py-2.5 font-sans font-semibold cursor-pointer"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="bg-white border border-line rounded-2xl p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)] flex flex-col gap-4"
              >
                {/* Honeypot — hidden from real users; bots that fill it are rejected */}
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={msg.website}
                  onChange={(e) => setMsg({ ...msg, website: e.target.value })}
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                />
                {FIELDS.map(({ k, l, t, p }) => (
                  <div key={k}>
                    <label className="text-[13px] font-semibold text-muted block mb-1.5">
                      {l}
                    </label>
                    <input
                      type={t}
                      placeholder={p}
                      value={msg[k]}
                      onChange={(e) => setMsg({ ...msg, [k]: e.target.value })}
                      required
                      className={inputCls}
                    />
                  </div>
                ))}
                <div>
                  <label className="text-[13px] font-semibold text-muted block mb-1.5">
                    Message *
                  </label>
                  <textarea
                    placeholder="Write your message here..."
                    value={msg.message}
                    onChange={(e) => setMsg({ ...msg, message: e.target.value })}
                    required
                    rows={5}
                    className={inputCls + " resize-y"}
                  />
                </div>
                {error && (
                  <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-3 py-2" role="alert">
                    {error}
                  </div>
                )}
                <Button type="submit" size="block" disabled={sending}>
                  {sending ? "Sending…" : "Send Message"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
